import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    TablePagination,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import proveedorService, { Proveedor } from '../services/proveedorService';

interface ProveedorSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (proveedor: Proveedor) => void;
}

const DEFAULT_PAGE_SIZE = 10;

const ProveedorSearch: React.FC<ProveedorSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [page, setPage] = useState(0); // 0-based para TablePagination
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [proveedores, setProveedores] = useState<Proveedor[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const resetState = () => {
        setSearchTerm('');
        setAppliedSearchTerm('');
        setPage(0);
        setPageSize(DEFAULT_PAGE_SIZE);
        setProveedores([]);
        setTotalCount(0);
        setError(null);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleSearchApply = () => {
        // Aplicar el término de búsqueda actual y volver a la primera página
        setPage(0);
        setAppliedSearchTerm(searchTerm.trim());
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSize = parseInt(event.target.value, 10) || DEFAULT_PAGE_SIZE;
        setPageSize(newSize);
        setPage(0);
    };

    const handleSelect = (proveedor: Proveedor) => {
        onSelect(proveedor);
        handleClose();
    };

    // Cargar proveedores cada vez que cambian criterios de búsqueda/paginación
    useEffect(() => {
        if (!open) {
            return;
        }

        let isActive = true;

        const loadProveedores = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await proveedorService.getProveedores({
                    searchTerm: appliedSearchTerm || undefined,
                    pageNumber: page + 1, // API es 1-based
                    pageSize
                });

                if (!isActive) return;

                setProveedores(response.items ?? []);
                setTotalCount(response.totalCount ?? 0);

                if ((response.items ?? []).length === 0) {
                    setError('No se encontraron proveedores.');
                }
            } catch (err: any) {
                if (!isActive) return;
                setError(`Error al cargar proveedores: ${err?.message || 'Error desconocido'}`);
                setProveedores([]);
                setTotalCount(0);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        loadProveedores();

        return () => {
            isActive = false;
        };
    }, [open, appliedSearchTerm, page, pageSize]);

    // Cuando se abre el diálogo por primera vez, cargar la primera página
    useEffect(() => {
        if (open) {
            setPage(0);
            setAppliedSearchTerm('');
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Proveedor
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de proveedor o nombre"
                        inputProps={{ 'aria-label': 'Buscar proveedor' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearchApply();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearchApply} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && proveedores.length > 0 && (
                    isSmallScreen ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '100%', overflowY: 'auto' }}>
                            {proveedores.map((prov) => (
                                <Paper
                                    key={prov.nroProveedor}
                                    variant="outlined"
                                    onClick={() => handleSelect(prov)}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        #{prov.nroProveedor}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word', mb: 1 }}>
                                        {prov.nombre || '-'}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        fullWidth
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelect(prov);
                                        }}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                                    >
                                        Seleccionar
                                    </Button>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: 400,
                                maxWidth: '100%',
                                overflowX: 'auto',
                                '& .MuiTableCell-root': {
                                    py: 1.5,
                                    fontSize: '0.875rem'
                                }
                            }}
                        >
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Nro. Proveedor</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Nombre</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">
                                            Acción
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {proveedores.map((prov) => (
                                        <TableRow
                                            key={prov.nroProveedor}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleSelect(prov)}
                                        >
                                            <TableCell>{prov.nroProveedor}</TableCell>
                                            <TableCell>{prov.nombre || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelect(prov);
                                                    }}
                                                >
                                                    Seleccionar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                )}

                {!loading && proveedores.length === 0 && !error && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron proveedores
                    </Typography>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 20]}
                                        labelRowsPerPage="Filas:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                        }
                        sx={{
                            '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 },
                        }}
                    />
                </Box>

                <Button
                    onClick={handleClose}
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProveedorSearch;

